import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import type {
	DiagramStatus,
	Point,
	DiagramTransition,
	DiagramSharedTransitionWithDefinedPorts,
	DiagramDirectTransitionWithDefinedPorts,
} from './toPortInfo.tsx';
import type { Positionable } from './ListEdgeDetails.tsx';
import {
	isDirectOrSharedTransitionWithDefinedPorts,
	isNilOrEmpty,
} from './isEveryPropertyDefined.tsx';
import { useLatest } from './useLatest.tsx';
import { calculateDefaultGlobalLoopsContainerPosition } from './calculateDefaultGlobalLoopsContainerPosition.tsx';
import { GLOBAL_LOOPS_CONTAINER_TYPE } from './directionKeys.tsx';
import {
	autoFitCanvas,
	findDefaultPositions,
	deferredExecute,
	definePortsOnDirectTransition,
	definePortsOnSharedTransition,
} from './getVerticalOffset.tsx';

export interface Options {
	transitions: DiagramTransition[];
	startNodePosition: Point | undefined;
	statuses: DiagramStatus[];
	statusWidths: Record<string, number>;
	isStatusWidthsReady: boolean;
	rootSvgRef: MutableRefObject<HTMLElement | null>;
	canvasContainerRef: MutableRefObject<HTMLElement | null>;
	providedGlobalLoopsContainerPosition: Point | undefined;
	onTransitionPortChange?: (transition: DiagramTransition) => void;
	onItemMove?: (payload: Positionable) => void;
	onPan: (point: Point) => void;
	onZoom: (zoom: number) => void;
	onClearGlobalLoopsContainerPosition: () => void;
}

export type TransitionsWithDefaultPorts = Record<
	string,
	DiagramDirectTransitionWithDefinedPorts | DiagramSharedTransitionWithDefinedPorts
>;

/** Handles assigning ports to transitions and status positions if they are not provided.
 * Currently this mimicks the existing behaviour of the old draw2d workflow diagram.
 *
 * Transition ports are determined using the angles between the connected ports.
 * Statuses are progressively placed in the middle right of the existing diagram elements.
 *
 */
export const useAutoLayout = ({
	transitions,
	startNodePosition,
	statuses,
	statusWidths,
	isStatusWidthsReady,
	rootSvgRef,
	canvasContainerRef,
	providedGlobalLoopsContainerPosition,
	onClearGlobalLoopsContainerPosition,
	onTransitionPortChange,
	onItemMove,
	onPan,
	onZoom,
}: Options) => {
	const latestStatuses = useLatest(statuses);
	const latestStatusWidths = useLatest(statusWidths);
	const hasInitialised = useRef<'pending' | 'started' | 'finished'>('pending');
	const [isLayoutStable, setIsLayoutStable] = useState(false);
	const doesDiagramContainGlobalLoops = transitions.some((x) => x.type === 'global-looped');
	const previousDoesDiagramContainGlobalLoops = usePrevious(doesDiagramContainGlobalLoops);

	const [defaultPositions, setDefaultPositions] = useState<Record<string, Point>>({});
	const [defaultGlobalLoopsContainerPosition, setDefaultGlobalLoopsContainerPosition] = useState<
		Point | undefined
	>();
	const [shouldAutoFit, setShouldAutoFit] = useState(false);

	const [transitionsWithDefaultPorts, setTransitionsWithDefaultPorts] =
		useState<TransitionsWithDefaultPorts>({});

	const latestDefaultPositions = useLatest(defaultPositions);
	const latestDefaultGlobalLoopsContainerPosition = useLatest(defaultGlobalLoopsContainerPosition);
	const latestStartNodePosition = useLatest(startNodePosition);
	const latestOnItemMove = useLatest(onItemMove);

	const autoFit = useCallback(() => {
		autoFitCanvas(rootSvgRef, canvasContainerRef, onPan, onZoom);

		deferredExecute(() => {
			setIsLayoutStable(true);
		});
	}, [canvasContainerRef, onPan, onZoom, rootSvgRef]);

	const autoPositionStatuses = useCallback(() => {
		const positioned = findDefaultPositions(
			latestStatusWidths.current,
			latestStartNodePosition.current,
			statuses,
			latestDefaultPositions.current,
		);

		if (positioned.length) {
			const newPositions = positioned.reduce<Record<string, Point>>((acc, positionable) => {
				acc[positionable.id] = positionable.position;
				return acc;
			}, {});

			setDefaultPositions((prev) => ({ ...prev, ...newPositions }));

			positioned.forEach((positionable) => {
				latestOnItemMove.current?.(positionable);
			});
		}

		return positioned;
	}, [
		latestDefaultPositions,
		latestOnItemMove,
		latestStatusWidths,
		latestStartNodePosition,
		statuses,
	]);

	useEffect(() => {
		// Effect to handle auto positioning of global loops container when global loop container is added or removed from diagram
		if (doesDiagramContainGlobalLoops) {
			if (!isNilOrEmpty(latestDefaultGlobalLoopsContainerPosition.current)) {
				// Exit early if global loops container already has a default position set
				return;
			}

			const defaultPosition = !isNilOrEmpty(providedGlobalLoopsContainerPosition)
				? providedGlobalLoopsContainerPosition
				: calculateDefaultGlobalLoopsContainerPosition(
						startNodePosition,
						statuses,
						defaultPositions,
					);

			setDefaultGlobalLoopsContainerPosition(defaultPosition);

			// Call container move callback to save the calculated default back to the component that mounts the accessible diagram
			defaultPosition &&
				latestOnItemMove.current?.({
					id: GLOBAL_LOOPS_CONTAINER_TYPE,
					type: GLOBAL_LOOPS_CONTAINER_TYPE,
					position: defaultPosition,
				});
		} else if (previousDoesDiagramContainGlobalLoops) {
			// When global loops are removed from the diagram, clear out the global loops position so a new auto position can be calculated when it is added back
			setDefaultGlobalLoopsContainerPosition(undefined);
			onClearGlobalLoopsContainerPosition();
		}
	}, [
		doesDiagramContainGlobalLoops,
		previousDoesDiagramContainGlobalLoops,
		latestDefaultGlobalLoopsContainerPosition,
		providedGlobalLoopsContainerPosition,
		startNodePosition,
		statuses,
		defaultPositions,
		latestOnItemMove,
		onClearGlobalLoopsContainerPosition,
	]);

	useEffect(() => {
		// On initialisation - auto position statuses after timeout so that status widths are available and always autofit
		if (hasInitialised.current === 'pending') {
			hasInitialised.current = 'started';
			if (!fg('concurrent-rendering-workflows-replace-settimeout')) {
				deferredExecute(() => {
					autoPositionStatuses();
					setShouldAutoFit(true);
					hasInitialised.current = 'finished';
				});
			}
		}

		if (
			hasInitialised.current === 'started' &&
			isStatusWidthsReady &&
			fg('concurrent-rendering-workflows-replace-settimeout')
		) {
			autoPositionStatuses();
			setShouldAutoFit(true);
			hasInitialised.current = 'finished';
		}

		// Subsequent additional statuses - auto position statuses and auto fit if status has been positioned
		if (hasInitialised.current === 'finished') {
			const positioned = autoPositionStatuses();
			if (positioned.length) {
				setShouldAutoFit(true);
			}
		}
	}, [
		autoPositionStatuses,
		latestDefaultPositions,
		latestOnItemMove,
		latestStatusWidths,
		startNodePosition,
		statuses,
		isStatusWidthsReady,
	]);

	useEffect(() => {
		// autofit after next render so that auto positioned statuses are in place
		if (shouldAutoFit) {
			autoFit();
			setShouldAutoFit(false);
		}
	}, [autoFit, shouldAutoFit]);

	/** Update transitions with undefined ports */
	useEffect(() => {
		const updatedTransitions: TransitionsWithDefaultPorts = {};

		const updateTransition = (
			transition: DiagramSharedTransitionWithDefinedPorts | DiagramDirectTransitionWithDefinedPorts,
		) => {
			updatedTransitions[transition.id] = transition;
			onTransitionPortChange?.(transition);
		};

		transitions.forEach((transition) => {
			if (
				transition.type === 'global-looped' ||
				transition.type === 'global' ||
				isDirectOrSharedTransitionWithDefinedPorts(transition)
			) {
				return;
			}

			if (transition.type === 'shared') {
				const positioned = definePortsOnSharedTransition(transition, latestStatuses.current);

				updateTransition(positioned);

				return;
			}

			const positioned = definePortsOnDirectTransition(transition, latestStatuses.current);

			updateTransition(positioned);
		});

		setTransitionsWithDefaultPorts(updatedTransitions);
	}, [transitions, latestStatuses, onTransitionPortChange]);

	return {
		isLayoutStable,
		defaultPositions,
		defaultGlobalLoopsContainerPosition,
		transitionsWithDefaultPorts,
	} as const;
};
